/**
 * PKCE (RFC 7636), écrit à la main plutôt qu'emprunté à une bibliothèque.
 *
 * Il n'y a rien à installer pour ça : `crypto.getRandomValues` et `crypto.subtle` viennent de `dom`,
 * déjà déclaré dans le `tsconfig` de la librairie, et ajouter une dépendance à un paquet publié que
 * six applications consomment se paie plus cher que quarante lignes.
 *
 * ⚠️ `crypto.subtle` n'existe que dans un **contexte sécurisé**. C'est vrai en HTTPS et vrai sur
 * `localhost`, donc vrai partout où ces applications tournent — mais pas sur une IP de réseau local
 * servie en clair.
 */

/** 32 octets : 43 caractères une fois encodés, soit le minimum que la RFC 7636 exige. */
const VERIFIER_BYTES = 32;

/** 16 octets suffisent pour un `state` : il est opaque et à usage unique. */
const STATE_BYTES = 16;

/**
 * base64url, sans remplissage. Le dépôt n'avait qu'un *décodeur*, enfoui dans `parseJwt` — il manquait
 * l'encodeur, et c'est lui dont le `code_challenge` a besoin.
 */
export function base64UrlEncode(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = '';
  view.forEach((byte) => (binary += String.fromCharCode(byte)));
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Un jeton aléatoire sûr pour un `code_verifier` ou un `state`.
 *
 * `crypto.getRandomValues` et non `Math.random` : le `state` est la protection CSRF du flux, et un
 * `code_verifier` devinable rendrait PKCE décoratif.
 */
export function randomUrlSafeToken(bytes: number = VERIFIER_BYTES): string {
  const buffer = new Uint8Array(bytes);
  crypto.getRandomValues(buffer);
  return base64UrlEncode(buffer);
}

export function randomState(): string {
  return randomUrlSafeToken(STATE_BYTES);
}

/** Le `code_challenge` en S256. `plain` n'est pas proposé : il n'apporte rien et la RFC le déconseille. */
export async function createCodeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier)
  );
  return base64UrlEncode(digest);
}
